import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Calendar, Users, FileText, Rocket, TrendingUp, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

// V5 Design Language
const v5 = {
  colors: {
    navy: '#1e1b4b',
    cream: '#FEF7ED',
    purple: '#8b5cf6',
    purpleDark: '#7c3aed',
    orange: '#f97316',
    green: '#22c55e',
    teal: '#14b8a6',
    amber: '#f59e0b',
  }
};

const AlphaThankYou = () => {
  return (
    <div style={{ backgroundColor: v5.colors.cream, minHeight: '100vh' }}>
      {/* Success Header */}
      <section className="py-24 relative overflow-hidden" style={{ paddingTop: '8rem' }}>
        {/* Floating Shapes - V5 Colors */}
        <motion.div
          className="absolute top-10 right-10 w-64 h-64 rounded-full"
          style={{
            background: `radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 70%)`,
            filter: 'blur(60px)'
          }}
          animate={{
            y: [0, 30, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        <div className="max-w-[820px] mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 150, delay: 0.2 }}
            className="inline-block mb-8"
          >
            <div 
              className="w-32 h-32 rounded-xl flex items-center justify-center mx-auto"
              style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}
            >
              <CheckCircle size={64} style={{ color: v5.colors.green }} />
            </div>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mb-6" 
            style={{
              fontFamily: "'Faustina', Georgia, serif",
              color: v5.colors.navy,
              fontSize: 'clamp(3rem, 8vw, 4.5rem)',
              fontWeight: 500,
              letterSpacing: '-0.03em'
            }}
          >
            You're In! 🎉
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mb-6" 
            style={{
              fontFamily: "'DM Sans', sans-serif",
              color: 'rgba(30, 27, 75, 0.7)',
              fontSize: 'clamp(1.2rem, 3vw, 1.5rem)',
              lineHeight: '1.6'
            }}
          >
            Thank you for applying to the Tappd Alpha Program. We're excited to help fill your tables!
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full"
            style={{
              backgroundColor: 'rgba(139, 92, 246, 0.1)',
              border: '2px solid rgba(139, 92, 246, 0.2)'
            }}
          >
            <Calendar size={24} style={{ color: v5.colors.purple }} />
            <p 
              className="text-xl font-bold"
              style={{
                fontFamily: "'DM Sans', sans-serif",
                color: v5.colors.purple
              }}
            >
              We'll contact you within 24 hours
            </p>
          </motion.div>
        </div>
      </section>

      {/* V5 Stripe divider */}
      <motion.div 
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1, delay: 1 }}
        className="h-1" 
        style={{
          background: `linear-gradient(to right, ${v5.colors.purple} 0%, ${v5.colors.orange} 100%)`
        }} 
      />

      {/* Onboarding Timeline */}
      <section className="py-24" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl mb-6" 
              style={{
                fontFamily: "'Faustina', Georgia, serif",
                fontWeight: 500,
                color: v5.colors.navy
              }}
            >
              What Happens Next?
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-xl" 
              style={{
                fontFamily: "'DM Sans', sans-serif",
                color: 'rgba(30, 27, 75, 0.6)'
              }}
            >
              Your Alpha POC onboarding timeline
            </motion.p>
          </div>

          <div className="space-y-6">
            {[
              {
                icon: Calendar,
                phase: 'Day 1-2',
                title: 'Kickoff Call',
                desc: "We'll schedule a 15-minute call to understand your business, finalize your offer, and answer any questions.",
                color: v5.colors.purple
              },
              {
                icon: FileText,
                phase: 'Day 3-4',
                title: 'Landing Page Setup',
                desc: 'We create your custom landing page with your offer, business info, and promo code. You review and approve.',
                color: v5.colors.orange
              },
              {
                icon: Users,
                phase: 'Day 5-7',
                title: 'Coaster Distribution',
                desc: "3,000 QR coasters with your business included go live at Ott's Tavern. Campaign officially launches!",
                color: v5.colors.teal
              },
              {
                icon: TrendingUp,
                phase: 'Week 2+',
                title: 'Weekly Reporting',
                desc: 'Receive weekly reports with QR scans, email captures, and estimated redemptions. Track your ROI in real-time.',
                color: v5.colors.amber
              },
              {
                icon: Rocket,
                phase: 'Week 4',
                title: 'Review & Scale',
                desc: 'We review results together. If you love it, we explore expanding to more venues. If not, no worries—zero obligation.',
                color: v5.colors.green
              }
            ].map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
                whileHover={{ x: 10 }}
              >
                <Card 
                  className="border-0"
                  style={{
                    backgroundColor: '#FFF',
                    borderRadius: '12px',
                    border: '1px solid rgba(30, 27, 75, 0.05)',
                    borderLeft: `4px solid ${step.color}`,
                    boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)'
                  }}
                >
                  <CardContent className="p-6 md:p-8">
                    <div className="flex items-start gap-6">
                      <motion.div
                        className="flex-shrink-0"
                        whileHover={{ rotate: 360, scale: 1.1 }}
                        transition={{ duration: 0.6 }}
                      >
                        <div 
                          className="w-16 h-16 md:w-20 md:h-20 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: step.color }}
                        >
                          <step.icon size={32} color="#FFFFFF" />
                        </div>
                      </motion.div>
                      <div className="flex-1">
                        <p 
                          className="text-sm font-bold mb-2"
                          style={{
                            fontFamily: "'DM Sans', sans-serif",
                            color: step.color,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em'
                          }}
                        >
                          {step.phase}
                        </p>
                        <h3 
                          className="text-2xl md:text-3xl mb-3"
                          style={{
                            fontFamily: "'Faustina', Georgia, serif",
                            fontWeight: 500,
                            color: v5.colors.navy
                          }}
                        >
                          {step.title}
                        </h3>
                        <p 
                          className="text-base md:text-lg"
                          style={{
                            fontFamily: "'DM Sans', sans-serif",
                            color: 'rgba(30, 27, 75, 0.6)',
                            lineHeight: '1.7'
                          }}
                        >
                          {step.desc}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* V5 Stripe divider */}
      <div 
        className="h-1" 
        style={{
          background: `linear-gradient(to right, ${v5.colors.purple} 0%, ${v5.colors.orange} 100%)`
        }} 
      />

      {/* Next Steps CTA */}
      <section className="py-24">
        <div className="max-w-[720px] mx-auto px-6 text-center">
          <motion.h3 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl mb-6" 
            style={{
              fontFamily: "'Faustina', Georgia, serif",
              fontWeight: 500,
              color: v5.colors.navy
            }}
          >
            In the Meantime...
          </motion.h3>
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg md:text-xl mb-10" 
            style={{
              fontFamily: "'DM Sans', sans-serif",
              color: 'rgba(30, 27, 75, 0.6)'
            }}
          >
            Check out how the customer experience works or explore our business page.
          </motion.p>
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link to="/">
              <motion.div
                whileHover={{ scale: 1.02, y: -3 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  className="px-8 py-3 text-base font-bold text-white"
                  style={{
                    background: `linear-gradient(135deg, ${v5.colors.purple}, ${v5.colors.purpleDark})`,
                    borderRadius: '12px',
                    boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
                  }}
                >
                  <span className="flex items-center gap-2">
                    See Customer Experience
                    <ArrowRight size={18} />
                  </span>
                </Button>
              </motion.div>
            </Link>
            <Link to="/for-businesses">
              <motion.div
                whileHover={{ scale: 1.02, y: -3 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  variant="outline"
                  className="px-8 py-3 text-base font-bold"
                  style={{
                    borderRadius: '12px',
                    border: `2px solid ${v5.colors.purple}`,
                    color: v5.colors.purple,
                    backgroundColor: 'transparent'
                  }}
                >
                  Back to Business Page
                </Button>
              </motion.div>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Bottom Gradient */}
      <div 
        className="h-1" 
        style={{
          background: `linear-gradient(to right, ${v5.colors.purple} 0%, ${v5.colors.orange} 100%)`
        }} 
      />
    </div>
  );
};

export default AlphaThankYou;
